import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import SvgTriangle from '@vio/atlas/foundation/illustrations/Triangle';
import ContentWrapper from '@vio/atlas/helpers/ContentWrapper';
import { Flex } from '@vio/atlas/helpers/Flex';
import { NoStyleButton } from '@vio/atlas/helpers/NoStyleButton';
import { NoStyleLink } from '@vio/atlas/helpers/NoStyleLink';
import { Icon } from '@vio/atlas/Icon';
import { PopUp } from '@vio/atlas/PopUp';
import { Text } from '@vio/atlas/Text';
import { AppleAppStoreBadge, GooglePlayBadge } from '@vio/shared/src/search/footer/AppButtonsWidget/AppButtonsWidget';
import { MinimalHotelCard } from 'components/MinimalHotelCard';
import useGenerateAppDownloadUrl from 'hooks/useGenerateAppDownloadUrl';
import hotelPlaceholderImage from 'images/placeholder-hotel-image.svg';
import { getLanguageCode } from 'modules/meta/selectors';
import { getWeb2AppPopupOverlay } from 'modules/overlay/selectors';
import { hideOverlay } from 'modules/overlay/slice';
import { getHotel, getHotelOfferEntity, getRoomsConfigFromOfferEntity } from 'modules/sapiSearch/selectors';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, Category, Entity } from '@daedalus/core/src/analytics/types/Events';
import { TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY } from '@daedalus/core/src/auth/modules/actions';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { OfferPromoKey } from '@daedalus/core/src/offer/types/offer';
import { DownloadAppQrCode } from './DownloadAppQrCode/DownloadAppQrCode';
const _exp = /*#__PURE__*/() => ContentWrapper;
const LeftColumn = /*#__PURE__*/styled(_exp())({
  name: "LeftColumn",
  class: "lx2piio",
  propsAsIs: true
});
const _exp2 = /*#__PURE__*/() => Flex.Column;
const HeaderWrapper = /*#__PURE__*/styled(_exp2())({
  name: "HeaderWrapper",
  class: "hyiqpll",
  propsAsIs: true
});
const _exp4 = /*#__PURE__*/() => Flex.Column;
const BottomWrapper = /*#__PURE__*/styled(_exp4())({
  name: "BottomWrapper",
  class: "bin9ld8",
  propsAsIs: true
});
const _exp6 = /*#__PURE__*/() => Flex.Row;
const RightColumn = /*#__PURE__*/styled(_exp6())({
  name: "RightColumn",
  class: "r1pwmmzk",
  propsAsIs: true
});
const BackgroundShapeWrapper = /*#__PURE__*/styled('div')({
  name: "BackgroundShapeWrapper",
  class: "bwehisg",
  propsAsIs: false
});
const _exp11 = /*#__PURE__*/() => NoStyleButton;
const CloseButton = /*#__PURE__*/styled(_exp11())({
  name: "CloseButton",
  class: "cstwbe4",
  propsAsIs: true
});
export const Web2AppPopup = () => {
  const dispatch = useDispatch();
  const overlayInfo = useSelector(getWeb2AppPopupOverlay);
  const {
    hotelId,
    placement
  } = overlayInfo || {};
  const hotel = useSelector(state => getHotel(state, hotelId));
  const offerEntity = useSelector(state => getHotelOfferEntity(state, hotelId));
  const lang = useSelector(getLanguageCode);
  const rooms = useSelector(state => getRoomsConfigFromOfferEntity(state, hotelId));
  const web2appPromo = offerEntity?.promos?.[OfferPromoKey.Web2App];
  const {
    generateAppStoreDeepLink,
    generatePlayStoreDeepLink
  } = useGenerateAppDownloadUrl();
  const onClose = useCallback(() => {
    dispatch(hideOverlay());
  }, [dispatch]);
  const handleClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    const targetElement = event.target as HTMLElement;
    const link = targetElement.closest('[data-id]') as HTMLLinkElement;
    const linkDataId = link?.dataset?.id;
    dispatch(trackEvent({
      category: Category.User,
      entity: Entity.AppInstallPopUpStoreLinks,
      action: Action.Clicked,
      payload: {
        selectedElement: link?.dataset?.id
      }
    }));
    const appDownloadUrl = linkDataId === 'AppleAppStoreBadge' ? generateAppStoreDeepLink(placement) : generatePlayStoreDeepLink(placement);
    setTimeout(() => {
      window.open(appDownloadUrl, '_blank');
    }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY);
  }, [dispatch, generateAppStoreDeepLink, generatePlayStoreDeepLink, placement]);
  return <PopUp headerVariant="floating" isOpen={Boolean(overlayInfo)} width={709} noPaddings closeAction={onClose} titleBarProps={{
    centerContent: undefined,
    endContent: <CloseButton onClick={onClose}>
            <Icon name="Close" />
          </CloseButton>
  }}>
      <Flex.Row>
        <LeftColumn paddingTop="s800" paddingX="s700" paddingBottom="s600">
          <HeaderWrapper gap="s200">
            <Text variant="titleXL" align="center">
              {hotel ? <FormattedMessageWrapper id="web2appPopup.accessThisExclusiveDeal" defaultMessage="Access this exclusive deal" /> : <FormattedMessageWrapper id="web2appPopup.scanTheQrCodeToDownloadOurMobileApp" defaultMessage="Scan this QR code to download our mobile app" />}
            </Text>
            <Text variant="bodyM" align="center" colorPath="content.neutral.c800">
              {hotel ? <FormattedMessageWrapper id="web2appPopup.useQrCodeToGetApp" defaultMessage="Scan the QR code to get the app and access this app-only deal:" /> : <FormattedMessageWrapper id="SUSI.joinMembers" defaultMessage="Join over 5,000,000 members who are saving on every booking" />}
            </Text>
          </HeaderWrapper>

          {hotel && <MinimalHotelCard fullWidth isCompactImage placeholderImage={hotelPlaceholderImage} imageUrl={hotel.imageURIs?.[0]} title={hotel.hotelName} rooms={rooms} hotelId={hotelId} offer={web2appPromo} />}

          <BottomWrapper gap="s300">
            <Text variant="bodyXS">
              <FormattedMessageWrapper id="web2appPopup.orGoToTheAppStore" defaultMessage="Or go to the App Store:" />
            </Text>
            <Flex.Row gap="s300" onClick={handleClick}>
              <NoStyleLink data-id="AppleAppStoreBadge">
                <AppleAppStoreBadge lang={lang} />
              </NoStyleLink>

              <NoStyleLink data-id="GooglePlayBadge">
                <GooglePlayBadge lang={lang} />
              </NoStyleLink>
            </Flex.Row>
          </BottomWrapper>
        </LeftColumn>
        <RightColumn alignItems="center" justifyContent="center">
          <BackgroundShapeWrapper>
            <SvgTriangle />
          </BackgroundShapeWrapper>
          <DownloadAppQrCode hotelId={hotelId} offer={web2appPromo} placement={placement} />
        </RightColumn>
      </Flex.Row>
    </PopUp>;
};

require("./Web2AppPopup.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./Web2AppPopup.tsx");